/**
 * The typefaces a Miro text item can be given, and how to draw them here
 * -------------------------------------------------------------
 * Miro's Web SDK takes a font as one of a fixed set of names
 * (`@mirohq/websdk-types`, `FontFamily`). Offering only those, under the names
 * Miro's own font menu shows, is what keeps a text on the Mood board page or
 * the canvas looking the same once it is on a board (Huanrui, 2026-10-12).
 *
 * Miro's "Brand fonts" (Aptos, Gotham, a client's own) are uploaded to the
 * company's Miro account and cannot be set through the SDK, so they are not
 * here. Two of Miro's own are left out too: Spoof and Tiempos Text are
 * licensed typefaces with no free web version to draw them with.
 *
 * `google` is the family's name on Google Fonts, loaded only when a text
 * actually uses it (loadMiroFont). The rest are on every computer already.
 */

export type MiroFontKey =
  | 'arial'
  | 'abril_fatface'
  | 'bangers'
  | 'caveat'
  | 'eb_garamond'
  | 'fredoka_one'
  | 'georgia'
  | 'graduate'
  | 'gravitas_one'
  | 'nixie_one'
  | 'noto_sans'
  | 'noto_serif'
  | 'open_sans'
  | 'permanent_marker'
  | 'plex_mono'
  | 'plex_sans'
  | 'plex_serif'
  | 'pt_sans'
  | 'pt_sans_narrow'
  | 'pt_serif'
  | 'rammetto_one'
  | 'roboto'
  | 'roboto_condensed'
  | 'roboto_mono'
  | 'roboto_slab'
  | 'times_new_roman'
  | 'titan_one'

export interface MiroFont {
  key: MiroFontKey
  /** As Miro's font menu writes it. */
  label: string
  /** The CSS font-family to draw it with here. */
  css: string
  /** Its family on Google Fonts, or absent when every computer has it. */
  google?: string
}

export const MIRO_FONTS: MiroFont[] = [
  { key: 'abril_fatface', label: 'Abril Fatface', css: "'Abril Fatface', Georgia, serif", google: 'Abril Fatface' },
  { key: 'arial', label: 'Arial', css: 'Arial, Helvetica, sans-serif' },
  { key: 'bangers', label: 'Bangers', css: "'Bangers', Impact, sans-serif", google: 'Bangers' },
  { key: 'caveat', label: 'Caveat', css: "'Caveat', cursive", google: 'Caveat' },
  { key: 'eb_garamond', label: 'EB Garamond', css: "'EB Garamond', Garamond, serif", google: 'EB Garamond' },
  { key: 'fredoka_one', label: 'Fredoka One', css: "'Fredoka One', 'Fredoka', sans-serif", google: 'Fredoka One' },
  { key: 'georgia', label: 'Georgia', css: 'Georgia, serif' },
  { key: 'graduate', label: 'Graduate', css: "'Graduate', serif", google: 'Graduate' },
  { key: 'gravitas_one', label: 'Gravitas One', css: "'Gravitas One', serif", google: 'Gravitas One' },
  { key: 'nixie_one', label: 'Nixie One', css: "'Nixie One', serif", google: 'Nixie One' },
  { key: 'noto_sans', label: 'Noto Sans', css: "'Noto Sans', sans-serif", google: 'Noto Sans' },
  { key: 'noto_serif', label: 'Noto Serif', css: "'Noto Serif', serif", google: 'Noto Serif' },
  { key: 'open_sans', label: 'Open Sans', css: "'Open Sans', sans-serif", google: 'Open Sans' },
  { key: 'permanent_marker', label: 'Permanent Marker', css: "'Permanent Marker', cursive", google: 'Permanent Marker' },
  { key: 'plex_sans', label: 'IBM Plex Sans', css: "'IBM Plex Sans', sans-serif", google: 'IBM Plex Sans' },
  { key: 'plex_serif', label: 'IBM Plex Serif', css: "'IBM Plex Serif', serif", google: 'IBM Plex Serif' },
  { key: 'plex_mono', label: 'IBM Plex Mono', css: "'IBM Plex Mono', monospace", google: 'IBM Plex Mono' },
  { key: 'pt_sans', label: 'PT Sans', css: "'PT Sans', sans-serif", google: 'PT Sans' },
  { key: 'pt_sans_narrow', label: 'PT Sans Narrow', css: "'PT Sans Narrow', sans-serif", google: 'PT Sans Narrow' },
  { key: 'pt_serif', label: 'PT Serif', css: "'PT Serif', serif", google: 'PT Serif' },
  { key: 'rammetto_one', label: 'Rammetto One', css: "'Rammetto One', sans-serif", google: 'Rammetto One' },
  { key: 'roboto', label: 'Roboto', css: "'Roboto', sans-serif", google: 'Roboto' },
  { key: 'roboto_condensed', label: 'Roboto Condensed', css: "'Roboto Condensed', sans-serif", google: 'Roboto Condensed' },
  { key: 'roboto_mono', label: 'Roboto Mono', css: "'Roboto Mono', monospace", google: 'Roboto Mono' },
  { key: 'roboto_slab', label: 'Roboto Slab', css: "'Roboto Slab', serif", google: 'Roboto Slab' },
  { key: 'times_new_roman', label: 'Times New Roman', css: "'Times New Roman', Times, serif" },
  { key: 'titan_one', label: 'Titan One', css: "'Titan One', sans-serif", google: 'Titan One' },
]

const BY_KEY = new Map(MIRO_FONTS.map((font) => [font.key, font]))

export function miroFont(key: string | undefined): MiroFont {
  return BY_KEY.get(key as MiroFontKey) ?? BY_KEY.get('open_sans')!
}

const loaded = new Set<string>()

/**
 * Make sure a Google font is on the page before it is drawn. Regular and bold
 * only: Miro's text has those two weights and no others.
 */
export function loadMiroFont(key: string | undefined): void {
  const font = miroFont(key)
  if (!font.google || loaded.has(font.google) || typeof document === 'undefined') return
  loaded.add(font.google)
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font.google).replace(/%20/g, '+')}:wght@400;700&display=swap`
  document.head.appendChild(link)
}

/**
 * Every typeface at once, in one request, for a typeface list that shows
 * each name in its own face (2026-10-12). Asked for the first time such a
 * list is opened; cheap after that.
 */
let previewsAsked = false
export function loadAllMiroFonts(): void {
  if (typeof document === 'undefined' || previewsAsked) return
  previewsAsked = true
  // Regular only: enough to show each name, and a family without a bold
  // cannot fail the whole request. A text set in one still loads it fully
  // (loadMiroFont), bold included.
  const names = MIRO_FONTS.map((font) => font.google).filter((name): name is string => Boolean(name))
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?${names
    .map((name) => `family=${encodeURIComponent(name).replace(/%20/g, '+')}`)
    .join('&')}&display=swap`
  document.head.appendChild(link)
}

/** The look of one text, shared by the Mood board page, the canvas and the Miro panel. */
export interface TextStyle {
  font: MiroFontKey
  /** In the units of whatever it sits on (the mood board's 1920 x 1080). */
  size: number
  bold: boolean
  align: 'left' | 'center' | 'right'
  /** #rrggbb */
  color: string
}
